/**
 * @file delivery-plan-details-helpers.ts
 * @module app/modules/orders-delivery/utils/delivery-plan-details-helpers
 * @description Pure helper functions extracted from delivery-plan-details.tsx:
 * plan-summary response parsing, vehicle/order merging, and unit formatting.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.2.1
 * @version 2.1.2
 */

import Method from '../../../../utils/methods';

/**
 * Resolves the most useful no-plan message from API response payload.
 *
 * Priority order:
 * 1) Top-level API message (`response.message`)
 * 2) Planner diagnostics message (`response.planData.plan.orderDeatils[0].msg`)
 */
export const getBackendNoPlanMessage = (apiResponse: any) => {
  const directMessage = apiResponse?.message;
  if (typeof directMessage === 'string' && directMessage.trim().length > 0) {
    return directMessage;
  }

  const diagnosticsMessage =
    apiResponse?.planData?.plan?.orderDeatils?.[0]?.msg;
  if (
    typeof diagnosticsMessage === 'string' &&
    diagnosticsMessage.trim().length > 0
  ) {
    return diagnosticsMessage;
  }
  return '';
};

export const getRoutePlanSummaryRecord = (apiResponse: any) => {
  if (!apiResponse) {
    return null;
  }
  return Array.isArray(apiResponse.record)
    ? apiResponse.record[0]
    : apiResponse.record;
};

/** Groups a truck's orders by refKey, combining processedVariants for duplicates. */
export const mergeOrders = (trucksWithOrders: any) => {
  return trucksWithOrders.map((truck: any) => {
    const orderGroups: any = {};
    truck.orders.forEach((order: any) => {
      const refKey = order.refKey;
      if (!orderGroups[refKey]) {
        orderGroups[refKey] = { ...order };
      } else {
        orderGroups[refKey].processedVariants = [
          ...orderGroups[refKey].processedVariants,
          ...order.processedVariants,
        ];
      }
    });

    const mergedOrders = Object.values(orderGroups);

    return {
      ...truck,
      orders: mergedOrders,
      consumedWeight: mergedOrders.reduce(
        (sum, order: any) => sum + order.weight,
        0,
      ),
    };
  });
};

/** Builds planner-shaped vehicle entries for trucks whose vehicle isn't in availableVehicles. */
export const extractMissingVehicles = async (data: any) => {
  const availableVehicleIds = new Set(
    data.availableVehicles.map((v: any) => v._id),
  );
  const missingVehicles: any = [];

  data.planData.plan.trucksWithOrders.forEach((truck: any) => {
    const vehicleData = truck.postData;

    if (!availableVehicleIds.has(vehicleData._id)) {
      const formattedVehicle: any = {
        _id: vehicleData._id || null,
        name: vehicleData.vModel?.name || '',
        number: vehicleData.vModel?.number || '',
        active: vehicleData.vModel?.onService ?? true,
        onService: vehicleData.vModel?.onService ?? true,
        routeId: truck.routeId,
        vehicleId: truck.vModel?.reference || '',
        orders: truck.orders,
        dimensions: {
          ...(vehicleData.vModel?.dimensions || {}),
          weight: vehicleData.vModel?.dimensions?.weight || 0,
          height: vehicleData.vModel?.dimensions?.height || 0,
          width: vehicleData.vModel?.dimensions?.width || 0,
          length: vehicleData.vModel?.dimensions?.length || 0,
        },
        ...(vehicleData.vModel?.image && { image: vehicleData.vModel.image }),
        user: vehicleData.dModel
          ? {
            _id: vehicleData.dModel.reference || null,
            name: vehicleData.dModel.name || '',
            ...(vehicleData.dModel.image && {
              image: vehicleData.dModel.image,
            }),
            active: true,
          }
          : null,
      };

      Object.keys(formattedVehicle).forEach((key) => {
        if (
          formattedVehicle[key] === null ||
          formattedVehicle[key] === undefined
        ) {
          delete formattedVehicle[key];
        }
      });

      missingVehicles.push(formattedVehicle);
    }
  });

  return missingVehicles;
};

/** Appends planner-derived vehicles missing from `data.availableVehicles`. */
export const addMissingVehiclesToAvailable = async (data: any) => {
  const missingVehicles = await extractMissingVehicles(data);
  return {
    ...data,
    availableVehicles: [...data.availableVehicles, ...missingVehicles],
  };
};

export const toSafeNumber = (value: any) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

export const kgToTons = (kilograms: any) => {
  const conversionFactor = 0.001;
  const tons = toSafeNumber(kilograms) * conversionFactor;
  return tons.toFixed(2);
};

export const getTotalTravelDuration = (ordersList: any[] = []) => {
  return ordersList.reduce(
    (total: number, order: any) => total + toSafeNumber(order?.duration),
    0,
  );
};

export const formatDurationSafe = (minutes: any) => {
  return Method.decimalHoursToHoursMinutesColon(toSafeNumber(minutes));
};

export const isTruthyPharmacyOrder = (value: any) => {
  return value === true || value === 'true' || value === 1 || value === '1';
};

export const getOrderTypeLabel = (orderVal: any) => {
  const isPharmacyOrder = isTruthyPharmacyOrder(
    orderVal?.isPharmacyOrder ?? orderVal?.postData?.isPharmacyOrder,
  );
  return isPharmacyOrder ? 'Pharmacy' : 'Utrade';
};

/** Filters order rows by pharmacy/utrade order type. */
export const applyOrderTypeFilter = (
  records: any[] = [],
  filterValue: string = 'all',
) => {
  if (filterValue === 'all') {
    return records;
  }

  if (filterValue === 'pharmacy') {
    return records.filter((orderVal: any) =>
      isTruthyPharmacyOrder(
        orderVal?.isPharmacyOrder ?? orderVal?.postData?.isPharmacyOrder,
      ),
    );
  }

  return records.filter(
    (orderVal: any) =>
      !isTruthyPharmacyOrder(
        orderVal?.isPharmacyOrder ?? orderVal?.postData?.isPharmacyOrder,
      ),
  );
};
